import LoadingPageIndicator from './LoadingPageIndicator';

interface SuspenseFallbackProps {
  /**
   * Type of loading indicator to display
   * - 'page': Full page loading (default) - uses LoadingPageIndicator
   * - 'content': Content area loading - centered in container
   * - 'inline': Small inline spinner
   */
  type?: 'page' | 'content' | 'inline';

  /**
   * Optional loading message to display below spinner
   */
  message?: string;
}

/**
 * Reusable Suspense fallback component with different display variants.
 * Wraps the existing LoadingPageIndicator with additional options.
 *
 * Usage:
 * <Suspense fallback={<SuspenseFallback />}>
 *   <MyComponent />
 * </Suspense>
 *
 * <Suspense fallback={<SuspenseFallback type="content" message="Laden..." />}>
 *   <MyComponent />
 * </Suspense>
 *
 * <Suspense fallback={<SuspenseFallback type="inline" />}>
 *   <MyComponent />
 * </Suspense>
 */
export function SuspenseFallback({ type = 'page', message }: SuspenseFallbackProps) {
  // Full page loading - uses existing LoadingPageIndicator
  if (type === 'page') {
    if (message) {
      return (
        <div className="d-flex flex-column align-items-center justify-content-center full-size">
          <div className="spinner-border" role="status">
            <span className="sr-only"></span>
          </div>
          <p className="mt-3 text-muted">{message}</p>
        </div>
      );
    }
    return <LoadingPageIndicator />;
  }

  // Content area loading - centered in container with padding
  if (type === 'content') {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center p-5">
        <div className="spinner-border" role="status">
          <span className="sr-only"></span>
        </div>
        {message && <p className="mt-3 text-muted">{message}</p>}
      </div>
    );
  }

  // Inline spinner - small, for inline loading states
  if (type === 'inline') {
    return (
      <div className="d-inline-flex align-items-center">
        <div className="spinner-border spinner-border-sm" role="status">
          <span className="sr-only"></span>
        </div>
        {message && <span className="ms-2 text-muted">{message}</span>}
      </div>
    );
  }

  // Fallback to page loading
  return <LoadingPageIndicator />;
}
